import { type FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
// Import Types
import type { FilterChip } from 'types';
// Import UI Components
import { Tag, Tooltip, Icon, SkeletonLoader, Scrollbar } from '@geneui/components';
// Import Styles
import styles from './filterChips.module.scss';

export interface FilterChipsProps {
    chips: FilterChip[];
    onChipRemove: (index: number) => void;
    className?: string;
}

/** Upper bound for the single chip row (never reached). The strip is horizontal-only; vertical scrolling is disabled in CSS. */
const CHIPS_ROW_MAX_HEIGHT = 60;

/**
 * Single-line strip of active filter chips ("Label = value ×").
 * Chips never wrap; the strip scrolls horizontally (gene-ui `Scrollbar` track) when it runs out of room.
 */
export const FilterChips: FC<FilterChipsProps> = ({ chips, onChipRemove, className }) => {
    const { t } = useTranslation();

    if (chips.length === 0) {
        return null;
    }

    return (
        <div className={classNames(styles.filterChips, className)}>
            <Scrollbar autoHeight={true} autoHeightMax={CHIPS_ROW_MAX_HEIGHT} size="small">
                <div className={styles.filterChipsRow}>
                    {chips.map(({ label, value, valueFieldKey }, index) => {
                        const text = `${t(label)} = ${value}`;
                        // The list is order-stable and a field can appear once, so field key + position is a stable key.
                        const key = `${valueFieldKey}-${index}`;

                        return isEmpty(String(value ?? '').replaceAll(',', '')) ? (
                            <SkeletonLoader key={key} isBusy={true} />
                        ) : (
                            <Tooltip key={key} text={text} position="bottom">
                                <div className={styles.filterChip}>
                                    <Tag
                                        name={text}
                                        appearance="light"
                                        size="big"
                                        cornerRadius="full-radius"
                                        cursor="default"
                                        icons={<Icon type="bc-icon-cancel" onClick={() => onChipRemove(index)} />}
                                    />
                                </div>
                            </Tooltip>
                        );
                    })}
                </div>
            </Scrollbar>
        </div>
    );
};
